import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import {
  Users, Baby, Clock, Calendar, Search, Filter, RefreshCw,
  ChevronDown, ChevronUp, Download, ArrowLeft, Phone, User,
  FileText, TrendingUp
} from 'lucide-react';
import { useToast } from '../hooks/use-toast';
import PrincipalNav from '../components/PrincipalNav';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const ObserverChildInteractions = () => {
  const navigate = useNavigate();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [data, setData] = useState(null);
  const [filters, setFilters] = useState({
    observer_id: '',
    child_id: '',
    date_from: '',
    date_to: ''
  });
  const [showFilters, setShowFilters] = useState(false);
  const [viewMode, setViewMode] = useState('interactions'); // interactions, by-observer, by-child

  useEffect(() => {
    const token = localStorage.getItem('principal_token');
    if (!token) {
      navigate('/principal/login');
      return;
    }
    loadData();
  }, [navigate]);

  const loadData = async () => {
    setLoading(true);
    try {
      const token = localStorage.getItem('principal_token');
      const params = new URLSearchParams({ token });
      
      if (filters.observer_id) params.append('observer_id', filters.observer_id);
      if (filters.child_id) params.append('child_id', filters.child_id);
      if (filters.date_from) params.append('date_from', filters.date_from);
      if (filters.date_to) params.append('date_to', filters.date_to);

      const response = await axios.get(`${BACKEND_URL}/api/principal/observer-child-interactions?${params}`);
      setData(response.data);
    } catch (error) {
      console.error('Error loading interactions:', error);
      toast({ title: "Error", description: "Failed to load interaction data", variant: "destructive" });
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    loadData();
  };

  const clearFilters = () => {
    setFilters({
      observer_id: '',
      child_id: '',
      date_from: '',
      date_to: ''
    });
    setTimeout(() => loadData(), 100);
  };

  const exportToCSV = () => {
    if (!data?.interactions?.length) return;

    const headers = ['Date', 'Time', 'Observer Name', 'Observer Email', 'Child Name', 'Child Age', 'Duration (mins)', 'Status'];
    const csvRows = [headers.join(',')];

    data.interactions.forEach(i => {
      const row = [
        i.date,
        i.time,
        `"${i.observer_name}"`,
        i.observer_email,
        `"${i.child_name}"`,
        i.child_age,
        i.duration_minutes || 0,
        i.status
      ];
      csvRows.push(row.join(','));
    });

    const csvContent = csvRows.join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `observer-child-interactions-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
    window.URL.revokeObjectURL(url);

    toast({ title: "Exported", description: "CSV file downloaded successfully" });
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-slate-50">
        <PrincipalNav />
        <div className="pt-20 flex items-center justify-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-violet-600"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-slate-50" data-testid="observer-child-interactions-page">
      <PrincipalNav />
      
      <div className="pt-20 pb-8 px-4">
        <div className="max-w-7xl mx-auto">
          {/* Header */}
          <div className="flex items-center justify-between mb-6">
            <div className="flex items-center gap-4">
              <Button variant="ghost" onClick={() => navigate('/principal/dashboard')}>
                <ArrowLeft className="w-4 h-4 mr-2" /> Back
              </Button>
              <div>
                <h1 className="text-2xl font-bold text-slate-800 flex items-center gap-2">
                  <Phone className="w-6 h-6 text-violet-600" />
                  Observer-Child Interactions
                </h1>
                <p className="text-slate-500">Track which observer talks to which child and when</p>
              </div>
            </div>
            <div className="flex gap-2">
              <Button variant="outline" onClick={() => setShowFilters(!showFilters)}>
                <Filter className="w-4 h-4 mr-2" /> Filters
                {showFilters ? <ChevronUp className="w-4 h-4 ml-1" /> : <ChevronDown className="w-4 h-4 ml-1" />}
              </Button>
              <Button variant="outline" onClick={exportToCSV} disabled={!data?.interactions?.length}>
                <Download className="w-4 h-4 mr-2" /> Export CSV
              </Button>
              <Button onClick={loadData} disabled={loading}>
                <RefreshCw className={`w-4 h-4 mr-2 ${loading ? 'animate-spin' : ''}`} /> Refresh
              </Button>
            </div>
          </div>

          {/* Filters Panel */}
          {showFilters && (
            <Card className="mb-6">
              <CardContent className="pt-6">
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Observer ID</label>
                    <Input
                      placeholder="Filter by observer..."
                      value={filters.observer_id}
                      onChange={(e) => setFilters({ ...filters, observer_id: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Child ID</label>
                    <Input
                      placeholder="Filter by child..."
                      value={filters.child_id}
                      onChange={(e) => setFilters({ ...filters, child_id: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Date From</label>
                    <Input
                      type="date"
                      value={filters.date_from}
                      onChange={(e) => setFilters({ ...filters, date_from: e.target.value })}
                    />
                  </div>
                  <div>
                    <label className="text-sm font-medium text-slate-700 mb-1 block">Date To</label>
                    <Input
                      type="date"
                      value={filters.date_to}
                      onChange={(e) => setFilters({ ...filters, date_to: e.target.value })}
                    />
                  </div>
                </div>
                <div className="flex gap-2 mt-4">
                  <Button onClick={applyFilters}>Apply Filters</Button>
                  <Button variant="outline" onClick={clearFilters}>Clear</Button>
                </div>
              </CardContent>
            </Card>
          )}

          {/* Summary Cards */}
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 mb-6">
            <Card className="bg-gradient-to-br from-violet-500 to-purple-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-violet-100 text-sm">Total Interactions</p>
                    <p className="text-3xl font-bold">{data?.total_interactions || 0}</p>
                  </div>
                  <Phone className="w-10 h-10 text-violet-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-blue-500 to-blue-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-blue-100 text-sm">Active Observers</p>
                    <p className="text-3xl font-bold">{data?.observer_summary?.length || 0}</p>
                  </div>
                  <Users className="w-10 h-10 text-blue-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-emerald-500 to-emerald-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-emerald-100 text-sm">Children Reached</p>
                    <p className="text-3xl font-bold">{data?.child_summary?.length || 0}</p>
                  </div>
                  <Baby className="w-10 h-10 text-emerald-200" />
                </div>
              </CardContent>
            </Card>
            <Card className="bg-gradient-to-br from-amber-500 to-amber-600 text-white border-0">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-amber-100 text-sm">Total Minutes</p>
                    <p className="text-3xl font-bold">
                      {data?.observer_summary?.reduce((sum, o) => sum + (o.total_minutes || 0), 0) || 0}
                    </p>
                  </div>
                  <Clock className="w-10 h-10 text-amber-200" />
                </div>
              </CardContent>
            </Card>
          </div>

          {/* View Mode Tabs */}
          <div className="flex gap-2 mb-4">
            <Button
              variant={viewMode === 'interactions' ? 'default' : 'outline'}
              onClick={() => setViewMode('interactions')}
              size="sm"
            >
              <FileText className="w-4 h-4 mr-2" /> All Interactions
            </Button>
            <Button
              variant={viewMode === 'by-observer' ? 'default' : 'outline'}
              onClick={() => setViewMode('by-observer')}
              size="sm"
            >
              <Users className="w-4 h-4 mr-2" /> By Observer
            </Button>
            <Button
              variant={viewMode === 'by-child' ? 'default' : 'outline'}
              onClick={() => setViewMode('by-child')}
              size="sm"
            >
              <Baby className="w-4 h-4 mr-2" /> By Child
            </Button>
          </div>

          {/* Content based on view mode */}
          {viewMode === 'interactions' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Phone className="w-5 h-5 text-violet-600" />
                  Interaction Log
                  <span className="text-sm font-normal text-slate-500">
                    ({data?.interactions?.length || 0} records)
                  </span>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!data?.interactions?.length ? (
                  <div className="text-center py-12 text-slate-400">
                    <Phone className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p className="text-lg">No interactions found</p>
                    <p className="text-sm">Sessions will appear here once observers start calling children</p>
                  </div>
                ) : (
                  <div className="overflow-x-auto">
                    <table className="w-full">
                      <thead>
                        <tr className="border-b bg-slate-50">
                          <th className="text-left p-3 font-medium text-slate-600">Date</th>
                          <th className="text-left p-3 font-medium text-slate-600">Time</th>
                          <th className="text-left p-3 font-medium text-slate-600">Observer</th>
                          <th className="text-left p-3 font-medium text-slate-600">Child</th>
                          <th className="text-left p-3 font-medium text-slate-600">Duration</th>
                          <th className="text-left p-3 font-medium text-slate-600">Status</th>
                        </tr>
                      </thead>
                      <tbody>
                        {data.interactions.slice(0, 100).map((interaction, idx) => (
                          <tr key={idx} className="border-b hover:bg-slate-50">
                            <td className="p-3">
                              <span className="flex items-center gap-2">
                                <Calendar className="w-4 h-4 text-slate-400" />
                                {interaction.date}
                              </span>
                            </td>
                            <td className="p-3">
                              <span className="flex items-center gap-2">
                                <Clock className="w-4 h-4 text-slate-400" />
                                {interaction.time}
                              </span>
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium text-slate-800">{interaction.observer_name}</p>
                                <p className="text-xs text-slate-500">{interaction.observer_email}</p>
                              </div>
                            </td>
                            <td className="p-3">
                              <div>
                                <p className="font-medium text-slate-800">{interaction.child_name}</p>
                                {interaction.child_age && (
                                  <p className="text-xs text-slate-500">Age: {interaction.child_age}</p>
                                )}
                              </div>
                            </td>
                            <td className="p-3">
                              <span className="px-2 py-1 bg-blue-100 text-blue-700 rounded text-sm">
                                {interaction.duration_minutes || 0} min
                              </span>
                            </td>
                            <td className="p-3">
                              <span className={`px-2 py-1 rounded text-sm ${
                                interaction.status === 'completed' ? 'bg-green-100 text-green-700' :
                                interaction.status === 'in_progress' ? 'bg-yellow-100 text-yellow-700' :
                                'bg-slate-100 text-slate-700'
                              }`}>
                                {interaction.status}
                              </span>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                    {data.interactions.length > 100 && (
                      <p className="text-center text-slate-500 text-sm mt-4">
                        Showing 100 of {data.interactions.length} interactions. Export CSV for full data.
                      </p>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {viewMode === 'by-observer' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Users className="w-5 h-5 text-blue-600" />
                  Summary by Observer
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!data?.observer_summary?.length ? (
                  <div className="text-center py-12 text-slate-400">
                    <Users className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No observer data available</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {data.observer_summary.map((observer, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:border-violet-300 transition-colors">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-violet-100 flex items-center justify-center">
                            <User className="w-5 h-5 text-violet-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{observer.observer_name}</p>
                            <p className="text-xs text-slate-500">Observer</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-violet-600">{observer.total_sessions}</p>
                            <p className="text-xs text-slate-500">Sessions</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-blue-600">{observer.unique_children}</p>
                            <p className="text-xs text-slate-500">Children</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-emerald-600">{observer.total_minutes}</p>
                            <p className="text-xs text-slate-500">Minutes</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}

          {viewMode === 'by-child' && (
            <Card>
              <CardHeader>
                <CardTitle className="text-lg flex items-center gap-2">
                  <Baby className="w-5 h-5 text-emerald-600" />
                  Summary by Child
                </CardTitle>
              </CardHeader>
              <CardContent>
                {!data?.child_summary?.length ? (
                  <div className="text-center py-12 text-slate-400">
                    <Baby className="w-16 h-16 mx-auto mb-4 opacity-50" />
                    <p>No child data available</p>
                  </div>
                ) : (
                  <div className="grid gap-4 md:grid-cols-2 lg:grid-cols-3">
                    {data.child_summary.map((child, idx) => (
                      <div key={idx} className="p-4 border rounded-lg hover:border-emerald-300 transition-colors">
                        <div className="flex items-center gap-3 mb-3">
                          <div className="w-10 h-10 rounded-full bg-emerald-100 flex items-center justify-center">
                            <Baby className="w-5 h-5 text-emerald-600" />
                          </div>
                          <div>
                            <p className="font-medium text-slate-800">{child.child_name}</p>
                            <p className="text-xs text-slate-500">Child</p>
                          </div>
                        </div>
                        <div className="grid grid-cols-3 gap-2 text-center">
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-emerald-600">{child.total_sessions}</p>
                            <p className="text-xs text-slate-500">Sessions</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-violet-600">{child.unique_observers}</p>
                            <p className="text-xs text-slate-500">Observers</p>
                          </div>
                          <div className="p-2 bg-slate-50 rounded">
                            <p className="text-xl font-bold text-blue-600">{child.total_minutes}</p>
                            <p className="text-xs text-slate-500">Minutes</p>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </CardContent>
            </Card>
          )}
        </div>
      </div>
    </div>
  );
};

export default ObserverChildInteractions;
